import {
	IconBrandApple,
	IconBrandUbuntu,
	IconBrandWindows,
	IconTerminal,
} from "@tabler/icons-react";
import {
	Children,
	type ComponentType,
	createContext,
	isValidElement,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const InOSTabsContext = createContext(false);
export const useInOSTabs = () => useContext(InOSTabsContext);

function detectOS(): string | null {
	if (typeof navigator === "undefined") return null;
	const ua = navigator.userAgent;
	if (ua.includes("Win")) return "Windows";
	if (ua.includes("Mac")) return "macOS";
	if (ua.includes("Linux")) return "Linux";
	return null;
}

const osIcons: Record<string, ComponentType<{ className?: string }>> = {
	windows: IconBrandWindows,
	macos: IconBrandApple,
	linux: IconBrandUbuntu,
};

function getIcon(os: string): ComponentType<{ className?: string }> {
	const key = os.toLowerCase();
	for (const [k, icon] of Object.entries(osIcons)) {
		if (key.includes(k)) return icon;
	}
	return IconTerminal;
}

interface OSTabProps {
	os: string;
	children: ReactNode;
}

export function OSTab({ children }: OSTabProps) {
	return <>{children}</>;
}

interface OSTabsProps {
	children: ReactNode;
}

export function OSTabs({ children }: OSTabsProps) {
	const tabs = useMemo(() => {
		const result: { os: string; content: ReactNode }[] = [];
		Children.forEach(children, (child) => {
			if (isValidElement<OSTabProps>(child) && child.props.os) {
				result.push({ os: child.props.os, content: child.props.children });
			}
		});
		return result;
	}, [children]);

	// Always start on tab 0 so prerender HTML matches the client hydration pass.
	const [activeTab, setActiveTab] = useState(0);

	useEffect(() => {
		const os = detectOS();
		if (!os) return;
		const matchIndex = tabs.findIndex((t) =>
			t.os.toLowerCase().includes(os.toLowerCase()),
		);
		if (matchIndex >= 0) {
			setActiveTab(matchIndex);
		}
	}, [tabs]);

	if (tabs.length === 0) return null;

	return (
		<InOSTabsContext.Provider value={true}>
			<div className="my-6 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
				<Tabs
					value={activeTab}
					onValueChange={(value) => setActiveTab(Number(value))}
				>
					<div className="border-b border-border bg-muted/50 px-1">
						<TabsList variant="line" className="gap-0">
							{tabs.map((tab, i) => {
								const Icon = getIcon(tab.os);
								return (
									<TabsTrigger
										key={tab.os}
										value={i}
										className="gap-1.5 px-3 py-2 text-xs font-medium"
									>
										<Icon className="h-3.5 w-3.5" />
										{tab.os}
									</TabsTrigger>
								);
							})}
						</TabsList>
					</div>
					{tabs.map((tab, i) => (
						<TabsContent
							key={tab.os}
							value={i}
							className="px-4 pb-2 pt-4 [&>*:first-child]:mt-0 [&_.group]:my-2 [&_pre]:my-2"
						>
							{tab.content}
						</TabsContent>
					))}
				</Tabs>
			</div>
		</InOSTabsContext.Provider>
	);
}
